/**
 * Regras de planilha: descobrir qual coluna traz o número do processo e quais
 * colunas escrever de volta.
 *
 * Só a decisão fica aqui — a leitura e a escrita do arquivo em si acontecem na
 * interface, para que a planilha do usuário nunca precise sair do navegador.
 */
import { apenasDigitos, extrairNumeros, interpretarNumeroProcesso } from "./numero-processo.js";
const juntar = (valores) => valores.join("; ");
const dataLegivel = (iso) => {
    if (!iso)
        return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return null;
    return d.toISOString().slice(0, 10).split("-").reverse().join("/");
};
export const COLUNAS_SAIDA = [
    { chave: "situacao", titulo: "Situação da consulta", padrao: true, extrair: () => "Encontrado" },
    { chave: "tribunal", titulo: "Tribunal", padrao: true, extrair: (r) => r.tribunal },
    { chave: "grau", titulo: "Grau", padrao: true, extrair: (r) => r.principal?.grauDescricao ?? null },
    { chave: "classe", titulo: "Classe", padrao: true, extrair: (r) => r.principal?.classe?.nome ?? null },
    { chave: "classeCodigo", titulo: "Código da classe", padrao: false, extrair: (r) => r.principal?.classe?.codigo ?? null },
    {
        chave: "assuntos",
        titulo: "Assuntos",
        padrao: true,
        extrair: (r) => juntar((r.principal?.assuntos ?? []).map((a) => a.nome)) || null,
    },
    { chave: "orgaoJulgador", titulo: "Órgão julgador", padrao: true, extrair: (r) => r.principal?.orgaoJulgador?.nome ?? null },
    { chave: "dataAjuizamento", titulo: "Data de ajuizamento", padrao: true, extrair: (r) => dataLegivel(r.dataAjuizamento) },
    {
        chave: "ultimoMovimento",
        titulo: "Último movimento",
        padrao: true,
        extrair: (r) => r.principal?.ultimoMovimento?.nome ?? null,
    },
    {
        chave: "dataUltimoMovimento",
        titulo: "Data do último movimento",
        padrao: true,
        extrair: (r) => dataLegivel(r.dataUltimoMovimento),
    },
    { chave: "totalMovimentos", titulo: "Total de movimentos", padrao: true, extrair: (r) => r.totalMovimentos },
    {
        chave: "dataUltimaAtualizacao",
        titulo: "Base atualizada em",
        padrao: true,
        extrair: (r) => dataLegivel(r.dataUltimaAtualizacao),
    },
    { chave: "diasEmTramitacao", titulo: "Dias em tramitação", padrao: false, extrair: (r) => r.diasEmTramitacao },
    { chave: "sigilo", titulo: "Nível de sigilo", padrao: false, extrair: (r) => r.principal?.nivelSigiloDescricao ?? null },
    { chave: "sistema", titulo: "Sistema", padrao: false, extrair: (r) => r.principal?.sistema?.nome ?? null },
    { chave: "formato", titulo: "Formato", padrao: false, extrair: (r) => r.principal?.formato?.nome ?? null },
    {
        chave: "graus",
        titulo: "Graus encontrados",
        padrao: false,
        extrair: (r) => juntar(r.graus.map((g) => g.grauDescricao)) || null,
    },
    { chave: "numeroFormatado", titulo: "Número (formatado)", padrao: false, extrair: (r) => r.numeroFormatado },
];
export const CHAVES_PADRAO = COLUNAS_SAIDA.filter((c) => c.padrao).map((c) => c.chave);
/** Palavras que sugerem uma coluna de número de processo, em ordem de preferência. */
const TITULOS_PROVAVEIS = [
    "numero unico",
    "n unico",
    "numero do processo",
    "numero processo",
    "num processo",
    "processo cnj",
    "numero cnj",
    "cnj",
    "processo",
    "numero",
    "autos",
];
function normalizarTitulo(titulo) {
    return titulo
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/gi, " ")
        .trim()
        .toLowerCase();
}
/**
 * Ordena as colunas da planilha pela chance de conterem o número do processo.
 *
 * O conteúdo pesa mais que o título: uma coluna cujas células trazem números
 * CNJ vence uma coluna apenas chamada "processo". O título entra como
 * desempate, o que resolve planilhas em que a mesma linha traz vários números.
 */
export function ranquearColunas(cabecalhos, linhas, tamanhoAmostra = 200) {
    const amostra = linhas.slice(0, tamanhoAmostra);
    return cabecalhos
        .map((titulo) => {
        let acertos = 0;
        let preenchidas = 0;
        for (const linha of amostra) {
            const valor = linha[titulo];
            if (valor === null || valor === undefined || String(valor).trim() === "")
                continue;
            preenchidas += 1;
            const texto = String(valor);
            if (extrairNumeros(texto).length > 0 || apenasDigitos(texto).length === 20)
                acertos += 1;
        }
        const proporcao = preenchidas === 0 ? 0 : acertos / preenchidas;
        const indiceTitulo = TITULOS_PROVAVEIS.indexOf(normalizarTitulo(titulo));
        const bonusTitulo = indiceTitulo === -1 ? 0 : (TITULOS_PROVAVEIS.length - indiceTitulo) / TITULOS_PROVAVEIS.length;
        return {
            titulo,
            acertos,
            amostra: preenchidas,
            pontuacao: proporcao * 10 + bonusTitulo,
        };
    })
        .sort((a, b) => b.pontuacao - a.pontuacao);
}
/** Coluna mais provável, ou `null` se nenhuma apresentar indício de processo. */
export function detectarColunaProcesso(cabecalhos, linhas) {
    const melhor = ranquearColunas(cabecalhos, linhas)[0];
    if (!melhor || melhor.pontuacao <= 0)
        return null;
    return melhor.titulo;
}
/**
 * Título livre para uma coluna nova, evitando sobrescrever colunas existentes.
 * `Tribunal` já usado vira `Tribunal (2)`.
 */
export function tituloDisponivel(desejado, jaUsados) {
    if (!jaUsados.has(desejado))
        return desejado;
    for (let i = 2; i < 1000; i += 1) {
        const alternativa = `${desejado} (${i})`;
        if (!jaUsados.has(alternativa))
            return alternativa;
    }
    return `${desejado} (${Date.now()})`;
}
/**
 * Primeiro número de processo reconhecível em uma célula.
 *
 * A célula pode trazer o número puro, mascarado, ou cercado de outro texto
 * ("Proc. 1234567-89.2024.8.26.0100 — cobrança").
 */
export function numeroDaCelula(valor) {
    const texto = String(valor ?? "").trim();
    if (texto === "")
        return null;
    const doTexto = extrairNumeros(texto)[0];
    if (doTexto)
        return doTexto;
    const interpretado = interpretarNumeroProcesso(texto);
    return interpretado.ok ? interpretado.numero.digitos : null;
}
/** Número de processo de cada linha, na ordem, e o conjunto único a consultar. */
export function numerosDaColuna(linhas, coluna) {
    const porLinha = linhas.map((linha) => numeroDaCelula(linha[coluna]));
    return { porLinha, unicos: [...new Set(porLinha.filter((n) => n !== null))] };
}
/**
 * Texto da coluna "Situação da consulta".
 *
 * É a coluna que explica por que as demais ficaram vazias em uma linha — sem
 * ela o usuário não distingue "processo sem movimentos" de "não consultado".
 */
export function descreverSituacao(numero, resultado) {
    if (numero === null)
        return "Sem número de processo na linha";
    if (resultado === undefined)
        return "Não consultado";
    switch (resultado.status) {
        case "encontrado":
            return "Encontrado";
        case "nao_encontrado":
            return "Não encontrado no DataJud";
        case "invalido":
            return `Número inválido: ${resultado.mensagem}`;
        case "erro":
            return `Erro na consulta: ${resultado.mensagem}`;
    }
}
/**
 * Acrescenta as colunas escolhidas às linhas originais.
 *
 * Nenhuma coluna do usuário é sobrescrita: um título que já exista na planilha
 * ganha um sufixo numérico. As linhas originais são copiadas, nunca alteradas.
 */
export function preencherPlanilha({ cabecalhos, linhas, numeroPorLinha, resultados, chaves, }) {
    const selecionadas = COLUNAS_SAIDA.filter((c) => chaves.includes(c.chave));
    const usados = new Set(cabecalhos);
    const titulosPorChave = new Map();
    for (const coluna of selecionadas) {
        const titulo = tituloDisponivel(coluna.titulo, usados);
        usados.add(titulo);
        titulosPorChave.set(coluna.chave, titulo);
    }
    const novasLinhas = linhas.map((original, indice) => {
        const numero = numeroPorLinha[indice] ?? null;
        const resultado = numero === null ? undefined : resultados.get(numero);
        const linha = { ...original };
        for (const coluna of selecionadas) {
            const titulo = titulosPorChave.get(coluna.chave);
            if (coluna.chave === "situacao") {
                linha[titulo] = descreverSituacao(numero, resultado);
            }
            else if (resultado?.status === "encontrado") {
                linha[titulo] = coluna.extrair(resultado.resumo);
            }
            else {
                linha[titulo] = null;
            }
        }
        return linha;
    });
    return {
        cabecalhos: [...cabecalhos, ...titulosPorChave.values()],
        linhas: novasLinhas,
        titulosPorChave,
    };
}
//# sourceMappingURL=planilha.js.map